import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Toolbar from './Toolbar';

import TableBody from '../BaseTable/TableBody';
import TableHead from './TableHead';

import { debounce } from '../../../helpers/utils';
import { showSpinner, hideSpinner } from '../../../src/helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(2),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  footer: {
    padding: theme.spacing(2),
  },
}));

const AsyncTable = ({
  url,
  headCells,
  defaultOrderField,
  defaultOrder = 'asc',
  customClicksHandler,
  customCellContentProvider,
  customLink,
  makeAjaxRequest,
  isAjax,
}) => {
  const classes = useStyles();
  const [rows, setRows] = React.useState([]);
  const [nextPageExists, setNextPageExists] = React.useState(false);
  const [order, setOrder] = React.useState(defaultOrder);
  const [fieldsSearching, setFieldsSearching] = React.useState('{}');
  const [fieldSort, setFieldSort] = React.useState(defaultOrderField);
  const [searchVisible, setSearchVisible] = React.useState(false);

  const loadRows = (params = {}, append = false) => {
    showSpinner();
    axios.get(`/${url}.json`, { params })
      .then((response) => {
        const { collection, nextPage } = response.data;
        setRows(append ? rows.concat(collection) : collection);
        setNextPageExists(nextPage);
        hideSpinner();
        return response;
      })
      .catch(() => {
        hideSpinner();
      });
  };
  const debouncedLoadRows = debounce(loadRows);

  React.useEffect(() => {
    loadRows({ fields_searching: '{}', order, field_sort: defaultOrderField });
  }, []);

  const takeCount = 20;

  const handleRequestSort = (event, fieldName) => {
    const newOrder = order === 'asc' ? 'desc' : 'asc';
    const data = {
      field_sort: fieldName, order: newOrder, take: takeCount, all_displayed: !nextPageExists,
    };
    if (fieldsSearching) {
      data.fields_searching = fieldsSearching;
    }

    setOrder(newOrder);
    setFieldSort(fieldName);

    loadRows(data);
  };

  const handleRequestSearch = () => {
    let jsonHash = {};
    Array.from(document.querySelectorAll('.MuiInputBase-input')).forEach((input) => {
      jsonHash[input.name] = input.value;
    });
    jsonHash = Object.fromEntries(Object.entries(jsonHash).filter(([_, v]) => v !== ''));
    const data = { fields_searching: jsonHash };

    if (fieldSort) {
      data.field_sort = fieldSort;
    }

    setFieldsSearching(jsonHash);
    debouncedLoadRows(data);
  };

  const intitializePaginationVariables = () => {
    let skipCount = 0;
    let prevRowsCount = rows.length;
    if (prevRowsCount) {
      skipCount = prevRowsCount;
    } else {
      prevRowsCount = takeCount;
    }
    return { skip: skipCount, take: takeCount, rowsCount: prevRowsCount };
  };

  const handleLoadMore = () => {
    const pageParams = intitializePaginationVariables();
    const data = { skip: pageParams.skip, take: pageParams.take };
    if (fieldsSearching) {
      data.fields_searching = fieldsSearching;
    }
    if (fieldSort) {
      data.field_sort = fieldSort;
    }
    if (order) {
      data.order = order;
    }
    loadRows(data, true);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Toolbar searchVisible={searchVisible} handleToggleVisibility={(v) => setSearchVisible(v)} />
        <TableContainer>
          <Table
            className={classes.table}
            size="medium"
          >
            <TableHead
              order={order}
              orderBy={fieldSort}
              onRequestSort={handleRequestSort}
              onRequestSearch={handleRequestSearch}
              headCells={headCells}
              searchVisible={searchVisible}
            />
            <TableBody
              rows={rows}
              headCells={headCells}
              customClicksHandler={customClicksHandler}
              customCellContentProvider={customCellContentProvider}
              customLink={customLink}
              makeAjaxRequest={makeAjaxRequest}
              isAjax={isAjax}
            />
          </Table>
        </TableContainer>
        {nextPageExists
            && (
              <div className={classes.footer}>
                <Button variant="outlined" color="primary" onClick={handleLoadMore}>
                  + More
                </Button>
              </div>
            )}
      </Paper>
    </div>
  );
};

AsyncTable.propTypes = {
  url: PropTypes.string.isRequired,
  headCells: PropTypes.arrayOf(PropTypes.shape).isRequired,
  customCellContentProvider: PropTypes.func,
  customClicksHandler: PropTypes.func,
  defaultOrderField: PropTypes.string,
  customLink: PropTypes.func,
  makeAjaxRequest: PropTypes.func,
  isAjax: PropTypes.bool,
};

AsyncTable.defaultProps = {
  customCellContentProvider: null,
  customClicksHandler: null,
  defaultOrderField: 'id',
  makeAjaxRequest: null,
  customLink: null,
  isAjax: false,
};

export default AsyncTable;
